import type { Place, Transport, TransportMode } from '@my-fullstack-app/itinerary-types';
import { minutesBetween, getEndTime } from '../components/itinerary/time-utils';
import { formatDuration } from './format';

/**
 * Sentinel `dayId` returned by `dayIdForPlace` for wishlist (unscheduled)
 * pins on the itinerary map. Single source so the maps' pin-styling
 * branch and the callers' tap handlers can't drift. Day ids are
 * generated (timestamp/random), so this literal can't collide with one.
 */
export const WISHLIST_DAY_ID = 'wishlist';

export interface RouteSegmentLabel {
  fromLat: number;
  fromLng: number;
  toLat: number;
  toLng: number;
  /** Human-readable label, e.g. "Walk · 12 min" */
  label: string;
  mode: TransportMode;
}

const TRANSPORT_MODE_LABEL: Record<TransportMode, string> = {
  walk: 'Walk',
  car: 'Drive',
  metro: 'Metro',
  train: 'Train',
  bus: 'Bus',
  bike: 'Bike',
  taxi: 'Taxi',
  ferry: 'Ferry',
  flight: 'Flight',
  other: 'Transfer',
};

export function clampRatio(ratio: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, ratio));
}

export function buildRouteSegmentLabels(
  places: Place[],
  autoTransports: Transport[],
): RouteSegmentLabel[] {
  return autoTransports.flatMap((t) => {
    const from = places.find((p) => p.id === t.fromPlaceId);
    const to = places.find((p) => p.id === t.toPlaceId);
    if (!from?.lat || !from.lng || !to?.lat || !to.lng) return [];
    const durationMin = minutesBetween(t.startTime, getEndTime(t, 30));
    return [{
      fromLat: from.lat,
      fromLng: from.lng,
      toLat: to.lat,
      toLng: to.lng,
      label: `${TRANSPORT_MODE_LABEL[t.mode]} · ${formatDuration(durationMin)}`,
      mode: t.mode,
    }];
  });
}
